import { type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { isProfileComplete } from "@/lib/profile";

/**
 * Gate for any "authenticated-only" page. Behaviour:
 *
 *   anonymous              -> redirect to /auth/login?next=<current>
 *   authed PARENT elsewhere -> redirect to /parent (parents don't have a
 *                              class_id and shouldn't land on student pages)
 *   authed STUDENT, incomplete profile (name / class / phone / school)
 *                           -> redirect to /onboarding (unless already
 *                              on /onboarding, otherwise we'd loop)
 *   loading                -> render a thin placeholder
 *
 * The signed-URL endpoints on the backend remain authoritative for
 * actual content access; this gate is just for the routes that
 * conceptually require sign-in (dashboard, settings, billing, etc.).
 * The parent portal itself (/parent/*) does its own role check
 * (mirrors AdminLayout) since it needs a "not authorized" panel rather
 * than a silent bounce for the reverse case (a student opening /parent).
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();
  const me = useCurrentUser();

  if (status === "loading" || (status === "authenticated" && me.isLoading)) {
    return (
      <div className="flex min-h-screen items-center justify-center text-text-muted text-sm">
        Loading…
      </div>
    );
  }

  if (status === "anonymous") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/auth/login?next=${next}`} replace />;
  }

  const onParentPortal = location.pathname.startsWith("/parent");
  if (!onParentPortal && status === "authenticated" && me.data?.role_id === "PARENT") {
    return <Navigate to="/parent" replace />;
  }

  // Authed student with an incomplete profile -> send them through onboarding
  // first. Also catches students who picked a class before name/phone were
  // required, or whose class was set by a back-office script.
  // (Don't redirect when we're already on /onboarding, or we'd loop.)
  const onOnboarding = location.pathname.startsWith("/onboarding");
  if (
    !onOnboarding &&
    status === "authenticated" &&
    me.data?.role_id === "STUDENT" &&
    !isProfileComplete(me.data)
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  return <>{children}</>;
}
